import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Sparkles, ArrowLeft, Plus, Pencil, Trash2, Users, FileText, TrendingUp } from 'lucide-react';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import axios from 'axios';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardPage = () => {
  const { user, token } = useAuth();
  const navigate = useNavigate();
  const [content, setContent] = useState([]);
  const [users, setUsers] = useState([]);
  const [stats, setStats] = useState({});
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingContent, setEditingContent] = useState(null);

  const [formData, setFormData] = useState({
    type: 'article',
    title: '',
    content: '',
    tags: ''
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const headers = { Authorization: `Bearer ${token}` };
      const [contentRes, statsRes] = await Promise.all([
        axios.get(`${API}/content`, { headers }),
        axios.get(`${API}/stats`, { headers })
      ]);

      setContent(contentRes.data);
      setStats(statsRes.data);

      if (user?.role === 'super_admin') {
        const usersRes = await axios.get(`${API}/users`, { headers });
        setUsers(usersRes.data);
      }
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        ...formData,
        tags: formData.tags.split(',').map(t => t.trim()).filter(Boolean)
      };

      if (editingContent) {
        await axios.put(`${API}/content/${editingContent.id}`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Content updated successfully');
      } else {
        await axios.post(`${API}/content`, payload, {
          headers: { Authorization: `Bearer ${token}` }
        });
        toast.success('Content created successfully');
      }

      setDialogOpen(false);
      setEditingContent(null);
      setFormData({ type: 'article', title: '', content: '', tags: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Operation failed');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this content?')) return;

    try {
      await axios.delete(`${API}/content/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Content deleted successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete content');
    }
  };

  const handleEdit = (item) => {
    setEditingContent(item);
    setFormData({
      type: item.type,
      title: item.title,
      content: item.content,
      tags: item.tags.join(', ')
    });
    setDialogOpen(true);
  };

  const handleUpdateUserRole = async (userId, newRole) => {
    try {
      await axios.put(`${API}/users/${userId}`, { role: newRole }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('User role updated successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to update user role');
    }
  };

  if (user?.role !== 'admin' && user?.role !== 'super_admin') {
    navigate('/search');
    return null;
  }

  return (
    <div className="min-h-screen bg-[radial-gradient(ellipse_at_top,_var(--tw-gradient-stops))] from-slate-900 via-background to-black">
      <nav className="border-b border-border/50 backdrop-blur-md bg-card/30">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Sparkles className="text-primary" size={28} />
            <h1 className="text-2xl font-heading font-bold text-foreground" data-testid="dashboard-title">
              concurrenz<span className="text-primary">.AI</span> - Dashboard
            </h1>
          </div>
          <Button data-testid="back-to-search-button" onClick={() => navigate('/search')} variant="ghost">
            <ArrowLeft size={20} className="mr-2" />
            Back to Search
          </Button>
        </div>
      </nav>

      <main className="max-w-7xl mx-auto px-6 py-12">
        {loading ? (
          <div className="text-center py-12">
            <div className="inline-block animate-spin text-primary">
              <Sparkles size={32} />
            </div>
          </div>
        ) : (
          <div className="space-y-8">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="stats-users">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-primary/20">
                    <Users className="text-primary" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Users</p>
                    <p className="text-2xl font-heading font-bold text-foreground">{stats.total_users || 0}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="stats-content">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-secondary/20">
                    <FileText className="text-secondary" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Content</p>
                    <p className="text-2xl font-heading font-bold text-foreground">{stats.total_content || 0}</p>
                  </div>
                </div>
              </Card>

              <Card className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid="stats-searches">
                <div className="flex items-center gap-4">
                  <div className="p-3 rounded-lg bg-accent/20">
                    <TrendingUp className="text-accent" size={24} />
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Total Searches</p>
                    <p className="text-2xl font-heading font-bold text-foreground">{stats.total_searches || 0}</p>
                  </div>
                </div>
              </Card>
            </div>

            <Tabs defaultValue="content" className="space-y-6">
              <TabsList className="bg-card/50 backdrop-blur-md">
                <TabsTrigger value="content" data-testid="content-tab">Content Management</TabsTrigger>
                {user?.role === 'super_admin' && (
                  <TabsTrigger value="users" data-testid="users-tab">User Management</TabsTrigger>
                )}
              </TabsList>

              <TabsContent value="content" className="space-y-6">
                <div className="flex justify-between items-center">
                  <h2 className="text-xl font-heading font-semibold text-foreground">Content Library</h2>
                  <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
                    <DialogTrigger asChild>
                      <Button data-testid="add-content-button" className="rounded-full" onClick={() => {
                        setEditingContent(null);
                        setFormData({ type: 'article', title: '', content: '', tags: '' });
                      }}>
                        <Plus size={18} className="mr-2" />
                        Add Content
                      </Button>
                    </DialogTrigger>
                    <DialogContent className="max-w-2xl">
                      <DialogHeader>
                        <DialogTitle data-testid="content-dialog-title">{editingContent ? 'Edit Content' : 'Add New Content'}</DialogTitle>
                      </DialogHeader>
                      <form onSubmit={handleSubmit} className="space-y-4">
                        <div className="space-y-2">
                          <Label>Type</Label>
                          <Select value={formData.type} onValueChange={(value) => setFormData({ ...formData, type: value })}>
                            <SelectTrigger data-testid="content-type-selector">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="article">Article</SelectItem>
                              <SelectItem value="faq">FAQ</SelectItem>
                              <SelectItem value="knowledge_base">Knowledge Base</SelectItem>
                              <SelectItem value="custom">Custom</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>

                        <div className="space-y-2">
                          <Label>Title</Label>
                          <Input
                            data-testid="content-title-input"
                            value={formData.title}
                            onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                            placeholder="Enter title"
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Content</Label>
                          <Textarea
                            data-testid="content-body-input"
                            value={formData.content}
                            onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                            placeholder="Enter content"
                            rows={8}
                            required
                          />
                        </div>

                        <div className="space-y-2">
                          <Label>Tags (comma-separated)</Label>
                          <Input
                            data-testid="content-tags-input"
                            value={formData.tags}
                            onChange={(e) => setFormData({ ...formData, tags: e.target.value })}
                            placeholder="tag1, tag2, tag3"
                          />
                        </div>

                        <Button data-testid="save-content-button" type="submit" className="w-full rounded-full">
                          {editingContent ? 'Update Content' : 'Create Content'}
                        </Button>
                      </form>
                    </DialogContent>
                  </Dialog>
                </div>

                <div className="grid grid-cols-1 gap-4" data-testid="content-list">
                  {content.length === 0 ? (
                    <Card className="p-12 text-center rounded-xl border bg-card/50 backdrop-blur-md">
                      <FileText className="mx-auto text-muted-foreground mb-4" size={48} />
                      <h3 className="text-xl font-heading font-semibold text-foreground mb-2">No Content Yet</h3>
                      <p className="text-muted-foreground">Start by adding your first piece of content</p>
                    </Card>
                  ) : (
                    content.map((item, idx) => (
                      <Card key={item.id} className="p-6 rounded-xl border bg-card/50 backdrop-blur-md hover:border-primary/50 transition-all" data-testid={`content-item-${idx}`}>
                        <div className="flex justify-between items-start">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="text-lg font-heading font-semibold text-foreground">{item.title}</h3>
                              <span className="text-xs px-2 py-1 rounded-full bg-secondary/20 text-secondary capitalize">
                                {item.type.replace('_', ' ')}
                              </span>
                            </div>
                            <p className="text-sm text-muted-foreground line-clamp-2 mb-3">{item.content}</p>
                            {item.tags.length > 0 && (
                              <div className="flex flex-wrap gap-2">
                                {item.tags.map((tag, i) => (
                                  <span key={i} className="text-xs px-2 py-1 rounded-full bg-primary/10 text-primary">
                                    {tag}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                          <div className="flex gap-2">
                            <Button
                              data-testid={`edit-content-${idx}`}
                              size="icon"
                              variant="ghost"
                              onClick={() => handleEdit(item)}
                            >
                              <Pencil size={18} />
                            </Button>
                            <Button
                              data-testid={`delete-content-${idx}`}
                              size="icon"
                              variant="ghost"
                              onClick={() => handleDelete(item.id)}
                            >
                              <Trash2 size={18} className="text-destructive" />
                            </Button>
                          </div>
                        </div>
                      </Card>
                    ))
                  )}
                </div>
              </TabsContent>

              {user?.role === 'super_admin' && (
                <TabsContent value="users" className="space-y-6">
                  <h2 className="text-xl font-heading font-semibold text-foreground">User Management</h2>
                  <div className="grid grid-cols-1 gap-4" data-testid="users-list">
                    {users.map((u, idx) => (
                      <Card key={u.id} className="p-6 rounded-xl border bg-card/50 backdrop-blur-md" data-testid={`user-item-${idx}`}>
                        <div className="flex justify-between items-center">
                          <div>
                            <h3 className="text-lg font-heading font-semibold text-foreground">{u.full_name}</h3>
                            <p className="text-sm text-muted-foreground">{u.email}</p>
                          </div>
                          <Select
                            value={u.role}
                            onValueChange={(value) => handleUpdateUserRole(u.id, value)}
                            disabled={u.id === user.id}
                          >
                            <SelectTrigger data-testid={`user-role-selector-${idx}`} className="w-40">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              <SelectItem value="user">User</SelectItem>
                              <SelectItem value="admin">Admin</SelectItem>
                              <SelectItem value="super_admin">Super Admin</SelectItem>
                            </SelectContent>
                          </Select>
                        </div>
                      </Card>
                    ))}
                  </div>
                </TabsContent>
              )}
            </Tabs>
          </div>
        )}
      </main>
    </div>
  );
};

export default DashboardPage;